import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";

export default function AdminFunnel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/funnel");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sortedSources = useMemo(
    () => Object.entries(data?.sources || {}).sort((a, b) => b[1] - a[1]),
    [data]
  );

  const sortedCallSources = useMemo(
    () => Object.entries(data?.calls?.by_source || {}).sort((a, b) => b[1] - a[1]),
    [data]
  );

  if (loading) return <div className="text-sm text-[#64748B]">Loading…</div>;
  if (!data) return <div className="text-sm text-red-600">Failed to load funnel.</div>;

  const total = data.total || 0;
  const stages = data.stages || {};
  const stageList = [
    { key: "new", label: "New leads", value: stages.new + (stages.contacted || 0), color: "bg-[#FFE5F2]", text: "text-[#D60079]" },
    { key: "contacted", label: "Contacted", value: stages.contacted || 0, color: "bg-blue-100", text: "text-blue-700" },
    { key: "qualified", label: "Qualified", value: stages.qualified || 0, color: "bg-amber-100", text: "text-amber-700" },
    { key: "won", label: "Won", value: stages.won || 0, color: "bg-green-100", text: "text-green-700" },
  ];

  return (
    <div className="space-y-6" data-testid="funnel-view">
      <div className="grid sm:grid-cols-3 gap-4">
        <Card label="Conversion Rate" value={`${data.conversion_rate}%`} hint="won ÷ total leads" />
        <Card label="Pipeline Value" value={`$${Number(data.pipeline_value || 0).toLocaleString()}`} hint="sum of estimates (excl. lost)" accent />
        <Card label="Recovered Value" value={`$${Number(data.won_value || 0).toLocaleString()}`} hint="sum of estimates on won" />
      </div>

      <div className="bg-white rounded-2xl border border-[#E2DDD3] p-6">
        <p className="text-xs uppercase tracking-wider text-[#64748B] mb-5">Funnel — last all time</p>
        <div className="space-y-3">
          {stageList.map((s) => {
            const pct = total ? Math.round((s.value / total) * 100) : 0;
            return (
              <div key={s.key}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-sm font-medium ${s.text}`}>{s.label}</span>
                  <span className="text-sm text-[#0F172A] tabular-nums">
                    <strong>{s.value}</strong> <span className="text-[#64748B]">· {pct}%</span>
                  </span>
                </div>
                <div className="w-full h-3 rounded-full bg-[#EFEAE0] overflow-hidden">
                  <div className={`h-full ${s.color}`} style={{ width: `${Math.max(2, pct)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-5 pt-4 border-t border-[#E2DDD3] grid grid-cols-2 gap-3 text-sm">
          <p><strong className="text-[#0F172A]">{stages.lost || 0}</strong> <span className="text-[#64748B]">lost</span></p>
          <p className="text-right">Win rate among decided: <strong className="text-[#0F172A]">{data.win_rate_among_decided}%</strong></p>
        </div>
      </div>

      {Object.keys(data.sources || {}).length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E2DDD3] p-6">
          <p className="text-xs uppercase tracking-wider text-[#64748B] mb-5">Lead sources</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sortedSources.map(([src, count]) => (
              <div key={src} className="flex items-center justify-between bg-[#F7F5F0] rounded-lg px-4 py-3">
                <span className="text-sm text-[#0F172A] capitalize">{src}</span>
                <span className="text-sm font-bold text-[#1E3A8A] tabular-nums">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phone-call tracking */}
      <div className="bg-white rounded-2xl border border-[#E2DDD3] p-6" data-testid="calls-panel">
        <div className="flex items-center justify-between mb-5">
          <p className="text-xs uppercase tracking-wider text-[#64748B]">Click-to-call tracking</p>
          <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Self-hosted · no third-party pixel</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-[#F7F5F0] rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-[#64748B]">All-time calls</p>
            <p className="mt-1 font-serif-display font-bold text-3xl text-[#0F172A] tabular-nums" data-testid="calls-total">
              {data.calls?.total || 0}
            </p>
          </div>
          <div className="bg-[#F7F5F0] rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-[#64748B]">Last 30 days</p>
            <p className="mt-1 font-serif-display font-bold text-3xl text-[#FF008F] tabular-nums" data-testid="calls-30d">
              {data.calls?.last_30_days || 0}
            </p>
          </div>
        </div>
        {sortedCallSources.length > 0 ? (
          <div className="mt-5">
            <p className="text-xs uppercase tracking-wider text-[#64748B] mb-3">Calls by source</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {sortedCallSources.map(([src, count]) => (
                <div key={src} className="flex items-center justify-between bg-[#F7F5F0] rounded-lg px-4 py-3">
                  <span className="text-sm text-[#0F172A] font-mono text-xs">{src}</span>
                  <span className="text-sm font-bold text-[#1E3A8A] tabular-nums">{count}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="mt-5 text-sm text-[#94A3B8]">No calls tracked yet. Data starts appearing once visitors click any phone number on the site.</p>
        )}
      </div>
    </div>
  );
}

function Card({ label, value, hint, accent }) {
  return (
    <div className={`bg-white rounded-2xl border border-[#E2DDD3] p-5 ${accent ? "ring-1 ring-[#FF008F]/30" : ""}`}>
      <p className="text-xs uppercase tracking-wider text-[#64748B]">{label}</p>
      <p className={`mt-2 font-serif-display font-bold text-3xl ${accent ? "text-[#FF008F]" : "text-[#0F172A]"}`}>{value}</p>
      <p className="text-xs text-[#94A3B8] mt-1">{hint}</p>
    </div>
  );
}
